import { MapPosition } from '../common'
import { AccountIntegration } from '../accountIntegration/types'
import { AccountBranch } from '../accountBranch/types'
import { GeoZone } from '../geoZone'

/**
 * Delivery types to distinguish between shipping and pickup options
 */
export enum DeliveryType {
    SHIPPING = 'SHIPPING',
    PICKUP = 'PICKUP',
}

/**
 * Entidad AccountDeliveryOption
 * Representa una opción de envío de una cuenta.
 */

export interface AccountDeliveryOption {
    id: string
    accountId: string
    accountBranchId: string
    name: string
    accountIntegrationId?: string
    isScheduled: boolean
    priceLogic: AccountDeliveryOptionPriceLogic
    status: AccountDeliveryOptionStatus
    deliveryType: DeliveryType
    demo: boolean
    hideAccountBranchAddress: boolean // Si true, no se muestra la direccion de la sucursal en el checkout.
    /** When true, estimated delivery times are calculated by AI and shown to customers at checkout */
    showEstimatedDeliveryTime: boolean
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
    data?: Record<string, unknown>

    deliveryZones: AccountDeliveryOptionZone[]
    integration?: AccountIntegration | null
    price?: number | null
    accountBranch?: AccountBranch | null
    /** Computed by api-public when listing delivery options; ISO date strings */
    estimatedDelivery?: {
        start: string
        end: string
    }
}

export enum AccountDeliveryOptionPriceLogic {
    FIXED = 'FIXED',
    BY_ZONE = 'BY_ZONE',
    PROVIDER = 'PROVIDER',
    /** @deprecated Use BY_ZONE instead */
    CALCULATED = 'CALCULATED',
}

export enum AccountDeliveryOptionStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export interface AccountDeliveryOptionCalculatedCost {
    basePrice: number
    distanceKm: number
    finalPrice: number
    priceLogic: AccountDeliveryOptionPriceLogic
    currency: string
}

export interface AccountDeliveryOptionZone {
    id: string
    accountId: string
    accountDeliveryOptionId: string
    geoZoneId: string
    price?: number | null
    status: AccountDeliveryOptionZoneStatus
    demo: boolean
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null

    // Relations
    geoZone?: GeoZone
}

export enum AccountDeliveryOptionZoneStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

// DTO Types for API operations
export interface DeliveryZoneInput {
    geoZoneId?: string
    geoZone?: GeoZoneInput
    price?: number | null
}

export interface GeoZoneInput {
    name: string
    area: MapPosition[]
    description?: string
}

export interface CreateAccountDeliveryOptionDTO {
    accountId: string
    accountBranchId: string
    name: string
    accountIntegrationId?: string | null
    isScheduled?: boolean
    priceLogic?: AccountDeliveryOptionPriceLogic
    price?: number
    status?: AccountDeliveryOptionStatus
    deliveryType: DeliveryType
    deliveryZones?: DeliveryZoneInput[]
    data?: Record<string, unknown>
    showEstimatedDeliveryTime?: boolean
}

export interface UpdateAccountDeliveryOptionDTO {
    accountBranchId: string
    name?: string
    accountIntegrationId?: string | null
    isScheduled?: boolean
    priceLogic?: AccountDeliveryOptionPriceLogic
    status?: AccountDeliveryOptionStatus
    deliveryType?: DeliveryType
    deliveryZones?: DeliveryZoneInput[]
    data?: Record<string, unknown>
    showEstimatedDeliveryTime?: boolean
}

/** Rule types for estimated delivery calculation */
export enum DeliveryOptionRuleType {
    PROCESSING_DAYS = 'PROCESSING_DAYS',
    SAME_DAY_CUTOFF = 'SAME_DAY_CUTOFF',
    DELIVERY_DAYS = 'DELIVERY_DAYS',
    PICKUP_READY_HOURS = 'PICKUP_READY_HOURS',
    FIXED_OFFSET_DAYS = 'FIXED_OFFSET_DAYS',
    /** ISO weekday numbers (1=Mon..7=Sun) that count as operating days */
    BUSINESS_DAYS = 'BUSINESS_DAYS',
    /** Delivery time-of-day window inferred by AI from historical orders */
    DELIVERY_HOURS = 'DELIVERY_HOURS',
    /** Extra days added to the pessimistic end when volume is high */
    BUFFER_SAFETY_MARGIN = 'BUFFER_SAFETY_MARGIN',
}

export interface AccountDeliveryOptionRule {
    id: string
    accountId: string
    accountDeliveryOptionId: string
    ruleType: DeliveryOptionRuleType
    params?: Record<string, unknown>
    priority: number
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

export interface CreateDeliveryOptionRuleDTO {
    ruleType: DeliveryOptionRuleType
    params?: Record<string, unknown>
    priority?: number
}

export interface UpdateDeliveryOptionRuleDTO {
    ruleType?: DeliveryOptionRuleType
    params?: Record<string, unknown>
    priority?: number
}

/** Params per rule type for estimation */
export interface ProcessingDaysParams {
    minDays: number
    maxDays?: number
}

export interface SameDayCutoffParams {
    cutoffTime: string // "HH:mm"
}

export interface DeliveryDaysParams {
    minDays: number
    maxDays: number
}

export interface PickupReadyHoursParams {
    hours?: number
    minHours?: number
    maxHours?: number
}

export interface FixedOffsetDaysParams {
    minDays: number
    maxDays?: number
}

export interface BusinessDaysParams {
    /** ISO weekday numbers: 1=Monday … 7=Sunday */
    days: number[]
}

export interface DeliveryHoursParams {
    /** Start of delivery window "HH:mm" */
    start: string
    /** End of delivery window "HH:mm" */
    end: string
}

export interface BufferSafetyMarginParams {
    /** Extra days added to the pessimistic (end) delivery date */
    extraDays: number
}

/** Unified delivery config shape used by the backoffice form */
export interface UnifiedDeliveryConfig {
    businessDays: number[]
    cutoffEnabled: boolean
    cutoffTime: string
    processingMinDays: number
    processingMaxDays: number
    deliveryMinDays: number
    deliveryMaxDays: number
    pickupHours: number
}
